import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Truck, Wrench, Zap, ChevronLeft, ChevronRight } from 'lucide-react';

const HomePage = () => {

  // Modern hero carousel with company and product images
  const heroImages = [
    {
      src: '/images/about/Amazon-filtration-company-pic.jpg',
      alt: 'Amazon Filtration Manufacturing Facility',
      title: 'Manufacturing Excellence',
      description: 'State-of-the-art facility producing premium filtration solutions',
      overlay: 'rgba(30, 64, 175, 0.7)'
    },
    {
      src: '/images/Air-Filters.jpeg',
      alt: 'Air Filters - High-performance air filtration solutions',
      title: 'Air Filters',
      description: 'Superior air filtration for engines and industrial applications',
      overlay: 'rgba(16, 185, 129, 0.7)'
    },
    {
      src: '/images/Industrial-Machines.jpg',
      alt: 'Industrial Machinery - Heavy-duty filtration systems',
      title: 'Industrial Solutions',
      description: 'Robust filtration systems for heavy machinery and equipment',
      overlay: 'rgba(239, 68, 68, 0.7)'
    },
    {
      src: '/images/Construction.jpg',
      alt: 'Construction Equipment - Reliable filtration technology',
      title: 'Construction & Mining',
      description: 'Durable filters for construction and mining equipment',
      overlay: 'rgba(245, 158, 11, 0.7)'
    },
    {
      src: '/images/Power-Energy.jpg',
      alt: 'Power & Energy - Advanced filtration systems',
      title: 'Power Generation',
      description: 'Specialized filtration for power generation equipment',
      overlay: 'rgba(139, 69, 19, 0.7)'
    },
    {
      src: '/images/automotive.jpg',
      alt: 'Automotive Industry - Professional filtration solutions',
      title: 'Automotive Excellence',
      description: 'Trusted by automotive professionals worldwide',
      overlay: 'rgba(147, 51, 234, 0.7)'
    }
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-rotate images every 5 seconds with smooth transitions
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const goToPrevious = () => {
    setCurrentImageIndex(currentImageIndex === 0 ? heroImages.length - 1 : currentImageIndex - 1);
  };

  const goToNext = () => {
    setCurrentImageIndex(currentImageIndex === heroImages.length - 1 ? 0 : currentImageIndex + 1);
  };

  const quickLinks = [
    {
      title: 'Products',
      description: 'Browse our comprehensive range of filtration products',
      icon: <Wrench className="w-8 h-8" />,
      path: '/products',
      color: 'bg-primary-600'
    },
    {
      title: 'Industries Served',
      description: 'See how we serve various industries worldwide',
      icon: <Truck className="w-8 h-8" />,
      path: '/industries',
      color: 'bg-industrial-600'
    },
    {
      title: 'Services',
      description: 'Discover our professional filtration services',
      icon: <Zap className="w-8 h-8" />,
      path: '/services',
      color: 'bg-green-600'
    }
  ];

  const features = [
    {
      icon: <Award className="w-12 h-12 text-primary-600" />,
      title: 'Reliable Quality',
      description: 'All our filters meet international standards and are rigorously tested for performance and durability.'
    },
    {
      icon: <Award className="w-12 h-12 text-primary-600" />,
      title: 'Industry Expertise',
      description: 'Over 3 years of experience serving automotive, construction, agriculture, and industrial sectors.'
    },
    {
      icon: <Users className="w-12 h-12 text-primary-600" />,
      title: 'Customer Focus',
      description: 'Dedicated support team providing technical consultation and after-sales service.'
    }
  ];

  const categories = [
    { name: 'Air Filters', image: '/images/Air-Filters.jpeg', count: '40+' },
    { name: 'Fuel Filters', image: '/images/Fuel-Filter.jpg', count: '30+' },
    { name: 'Oil Filters', image: '/images/Oil-Filter.jpg', count: '50+' },
    { name: 'Hydraulic Return Filters', image: '/images/Hydraulic-Return-Filter.jpg', count: '25+' },
    { name: 'Coolant Filters', image: '/images/Coolant-Filter.jpg', count: '20+' },
    { name: 'Cabin Filters', image: '/images/Cabin-Filter.jpg', count: '15+' }
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* SEO: Organization JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Amazon Filtration (K) Ltd',
        url: 'https://amazonfiltration.co.ke',
        logo: 'https://amazonfiltration.co.ke/logo.png',
        sameAs: [
          'https://www.facebook.com/',
          'https://www.linkedin.com/'
        ],
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Lokitang Road, Off Likoni Rd, Shed 18-Kenya Industrial Estates',
          addressLocality: 'Nairobi',
          addressCountry: 'KE'
        },
        contactPoint: [{
          '@type': 'ContactPoint',
          telephone: '+254714752613',
          contactType: 'customer service',
          areaServed: 'KE'
        }]
      }) }} />
      {/* Modern Hero Section with Dynamic Images */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Dynamic Image Carousel */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1500 ease-in-out ${
                index === currentImageIndex ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
              }`}
              style={{
                backgroundImage: `url('${image.src}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                filter: 'brightness(0.7) contrast(1.2) saturate(1.3)'
              }}
            />
          ))}
          {/* Dynamic Color Overlay */}
          <div 
            className="absolute inset-0 transition-all duration-1500 ease-in-out"
          style={{
              background: `linear-gradient(135deg, ${heroImages[currentImageIndex]?.overlay || 'rgba(30, 64, 175, 0.7)'}, rgba(0, 0, 0, 0.6))`
            }}
          ></div>
        </div>

        {/* Modern Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full p-4 text-white transition-all duration-300 hover:scale-110 shadow-lg border border-white/20"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full p-4 text-white transition-all duration-300 hover:scale-110 shadow-lg border border-white/20"
          aria-label="Next image"
        >
          <ChevronRight className="w-7 h-7" />
        </button>

        {/* Modern Image Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-4 h-4 rounded-full transition-all duration-500 ${
                index === currentImageIndex 
                  ? 'bg-white scale-125 shadow-lg' 
                  : 'bg-white/40 hover:bg-white/60 hover:scale-110'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
        
        {/* Modern Content with Dynamic Animations */}
        <div className="relative z-10 text-center text-white px-4 max-w-7xl mx-auto py-20">
          <div className="space-y-8">
            {/* Animated Main Heading */}
            <div className="space-y-6 animate-fade-in-up">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
                Your Trusted Supplier For
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-green-400 animate-gradient">
                  Machine Filtration
                </span>
          </h1>
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-200 max-w-5xl mx-auto leading-relaxed font-light">
                Reliable Filtration Solutions Since 2020
              </p>
            </div>

            {/* Dynamic Content with Smooth Transitions */}
            <div className="space-y-6 min-h-[120px] flex flex-col justify-center animate-fade-in-up">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold transition-all duration-1000 ease-in-out">
                {heroImages[currentImageIndex].title}
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto transition-all duration-1000 ease-in-out font-light">
                {heroImages[currentImageIndex].description}
              </p>
            </div>

            {/* Modern CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-4 animate-fade-in-up">
              <Link 
                to="/products" 
                className="group bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-10 py-5 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center gap-3 shadow-lg"
              >
                <Wrench className="w-6 h-6" />
                Explore Products
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
              <Link 
                to="/contact" 
                className="group bg-transparent border-2 border-white/80 text-white hover:bg-white hover:text-gray-900 px-10 py-5 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl backdrop-blur-sm flex items-center gap-3"
              >
                <Users className="w-6 h-6" />
                Get Quote
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              Explore Our Solutions
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              Discover our comprehensive range of filtration products and services designed for your industry needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {quickLinks.map((link, index) => (
              <Link
                key={index}
                to={link.path}
                className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 p-6 md:p-8 text-center"
              >
                <div className={`${link.color} w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-white mx-auto mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {link.icon}
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">{link.title}</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">{link.description}</p>
                <div className="flex items-center justify-center text-primary-600 font-medium group-hover:text-primary-700 text-sm md:text-base">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              Why Choose Amazon Filtration?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              We deliver excellence through quality, expertise, and customer commitment
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-4 md:p-6">
                <div className="flex justify-center mb-4 md:mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">{feature.title}</h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              Our Product Categories
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive range of filtration solutions for every application
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {categories.map((category, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="aspect-w-16 aspect-h-12">
                  <img
                    src={category.image}
                    alt={`${category.name} for industrial applications`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-48 sm:h-56 md:h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/400x300/1e40af/ffffff?text=${encodeURIComponent(category.name)}`;
                      console.log(`Failed to load image for ${category.name}: ${category.image}`);
                    }}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-white">
                  <h3 className="text-lg md:text-xl font-semibold mb-2">{category.name}</h3>
                  <p className="text-xs md:text-sm text-gray-200 mb-3 md:mb-4">{category.count} Products Available</p>
                  <Link
                    to={`/products?category=${category.name}`}
                    className="inline-flex items-center text-primary-300 hover:text-primary-200 font-medium text-sm md:text-base"
                  >
                    View Products
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-primary-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">
            Ready to Find Your Perfect Filtration Solution?
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-primary-100 mb-6 md:mb-8 max-w-2xl mx-auto">
            Contact our experts today for personalized recommendations and competitive pricing
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
            <Link
              to="/contact"
              className="bg-white text-primary-600 hover:bg-gray-100 font-medium py-3 md:py-4 px-6 md:px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm md:text-base"
            >
              Get Free Quote
            </Link>
            <Link
              to="/products"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 md:py-4 px-6 md:px-8 rounded-lg transition-all duration-300 transform hover:scale-105 text-sm md:text-base"
            >
              Browse Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
